// Full-page paywall gate for the /draw workspace. Rendered instead of the
// Freehand workspace when the signed-in user (or guest) doesn't own the
// Freehand Pro entitlement. The `Upgrade` button drives the same
// UpgradeDialog + Stripe checkout used elsewhere in the app; it just passes
// plan="freehand" so the backend charges $29.99 and flips the correct flag.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PenLine, Boxes, LogIn, LogOut, Lock, Check, Sparkles } from 'lucide-react';
import UpgradeDialog from '@/components/studio/UpgradeDialog';
import { useAuth } from '@/lib/auth';

const FEATURES = [
  'Draw panels directly on the 3D ball with your mouse or finger',
  'Freeform · Polyline · Shape stamp (square, hex, pentagon, diamond, circle)',
  'Symmetry: Mirror X / Y / Z · Antipodal · Ring',
  'Punch-through holes with perfectly matching stitch counts',
  'Move / scale / rotate / duplicate any panel',
  'Grid & edge snapping · reference photo tracing',
  'SVG & DXF flat-pattern exports — with holes',
  'Save unlimited Freehand designs to your gallery',
];

export default function FreehandPaywall() {
  const { user, login, logout } = useAuth();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  return (
    <div
      className="h-screen w-screen overflow-hidden flex flex-col bg-[#F8F9FA]"
      data-testid="freehand-paywall-root"
    >
      <header className="h-14 shrink-0 border-b border-slate-200 bg-white px-4 sm:px-6 flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
            <PenLine className="w-4 h-4 text-white" />
          </div>
          <div className="font-semibold tracking-tight text-slate-900">Freehand Draw</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/"
            className="hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-full border border-slate-200 hover:bg-slate-50 text-sm text-slate-700"
            data-testid="paywall-back-to-studio"
          >
            <Boxes className="w-4 h-4" /> Back to Studio
          </Link>
          {user ? (
            <button
              onClick={logout}
              className="flex items-center gap-2 pl-1 pr-2 h-9 rounded-full border border-slate-200 hover:bg-slate-50"
              data-testid="paywall-btn-logout"
              title={user.email}
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-300" />
              )}
              <LogOut className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
            </button>
          ) : (
            <Button
              variant="ghost"
              className="rounded-full h-9 px-3"
              onClick={login}
              data-testid="paywall-btn-login"
            >
              <LogIn className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign in</span>
            </Button>
          )}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-14 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold tracking-wide uppercase">
              <Lock className="w-3.5 h-3.5" /> Freehand Pro
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
              Draw your own panels
              <br />
              <span className="text-[#007AFF]">right on the ball.</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-lg">
              A drawing surface built for panel-makers — mirror, punch, stamp, stitch,
              and export production-ready SVG &amp; DXF flat patterns.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:pl-6">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <Sparkles className="w-4 h-4 text-[#007AFF]" /> One-time purchase
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-5xl font-semibold tracking-tight text-slate-900">$29.99</span>
                <span className="text-slate-500 text-sm">USD · yours forever</span>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                Separate from Studio Pro. Buy once, use forever — no subscription, no expiry.
              </p>
              <Button
                onClick={() => setUpgradeOpen(true)}
                className="mt-5 w-full rounded-full h-12 bg-[#007AFF] hover:bg-[#0062CC] text-white text-base"
                data-testid="paywall-btn-upgrade"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {user ? 'Unlock Freehand Draw' : 'Sign in to unlock'}
              </Button>
              <div className="mt-3 text-center text-xs text-slate-400">
                Powered by Stripe · secure checkout
              </div>
              <div className="mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500">
                Prefer the geometric templates?{' '}
                <Link to="/" className="text-[#007AFF] font-medium hover:underline" data-testid="paywall-link-studio">
                  Head to Studio
                </Link>
                .
              </div>
            </div>
          </div>
        </div>
      </main>

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        plan="freehand"
      />
    </div>
  );
}
